import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import JsonLd from "@/components/base/JsonLd";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import CommentSection from "@/pages/detail/components/CommentSection";
import RelatedTips from "@/pages/detail/components/RelatedTips";
import { categories } from "@/mocks/categories";
import { siteInfo } from "@/mocks/site";
import { tips } from "@/mocks/tips";
import { comments } from "@/mocks/comments";
import type { CommentItem, Tip } from "@/types/content";

const tipList: Tip[] = tips;
const commentMap = comments as unknown as Record<string, CommentItem[]>;

export default function Detail() {
  const { id } = useParams();
  const tip = tipList.find((item) => item.id === id);
  const [liked, setLiked] = useState(false);
  const [shareNotice, setShareNotice] = useState("");

  if (!tip) {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-file-search-line text-3xl" aria-hidden="true" />
        </span>
        <h1 className="mt-5 font-display text-2xl text-foreground-950">这条爆料不在了</h1>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600">
          它可能已经被合并或删除。你可以回到爆料广场，看看其他正在讨论的内容。
        </p>
        <Link
          to="/feed"
          className="mt-6 inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回爆料广场
        </Link>
      </div>
    );
  }

  const category = categories.find((item) => item.slug === tip.category);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const detailPath = `/detail/${tip.id}`;
  const related = tipList
    .filter((item) => item.id !== tip.id && item.category === tip.category)
    .slice(0, 3);
  const relatedList = related.length > 0 ? related : tipList.filter((item) => item.id !== tip.id).slice(0, 3);
  const initialComments = commentMap[tip.id] ?? [];

  const datePublished = `${tip.createdAt.replace(" ", "T")}:00+08:00`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: tip.title,
    description: tip.summary,
    image: [tip.cover],
    datePublished,
    dateModified: datePublished,
    articleSection: category?.label,
    keywords: tip.tags.join(","),
    author: { "@type": "Person", name: tip.author },
    publisher: { "@type": "Organization", name: siteInfo.name },
    mainEntityOfPage: `${origin}${detailPath}`,
  };

  const handleShare = () => {
    const url = `${origin}${detailPath}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => setShareNotice("链接已复制，去分享给朋友吧"))
        .catch(() => setShareNotice("复制失败，请手动复制地址栏链接"));
    } else {
      setShareNotice("请手动复制地址栏链接");
    }
    window.setTimeout(() => setShareNotice(""), 3200);
  };

  return (
    <div className="bg-background-100">
      <SeoHead
        title={`${tip.title} | 爆料专区`}
        description={`${tip.summary} 来自「${category?.label ?? "爆料专区"}」分类的匿名爆料，欢迎在评论区参与互动留言。`}
        keywords={`${tip.tags.join(",")},${category?.label ?? "爆料"},评论区互动,爆料专区`}
        path={detailPath}
        image={tip.cover}
        type="article"
      />
      <JsonLd data={schema} />

      <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-12">
        <Breadcrumbs
          items={[{ label: "首页", to: "/" }, { label: "爆料广场", to: "/feed" }, { label: tip.title }]}
        />

        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <article className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-2 font-label text-xs">
              <Link
                to={`/feed?category=${tip.category}`}
                className="rounded-full bg-secondary-100 px-3 py-1 text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                {category?.label}
              </Link>
              <span className="rounded-full bg-primary-100 px-3 py-1 text-primary-700">网友爆料</span>
              <span className="text-foreground-500">{tip.createdAt}</span>
            </div>

            <h1 className="mt-4 text-balance font-display text-2xl leading-snug text-foreground-950 md:text-4xl">
              {tip.title}
            </h1>
            <p className="mt-4 border-l-2 border-primary-400 pl-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              {tip.summary}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 border-y border-background-200 py-3 font-label text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-3-line" aria-hidden="true" />
                {tip.author}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-map-pin-2-line" aria-hidden="true" />
                {tip.location}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-eye-line" aria-hidden="true" />
                {tip.views} 次阅读
              </span>
            </div>

            <div className="mt-6 h-64 w-full overflow-hidden rounded-lg bg-background-200 md:h-[380px]">
              <img
                src={tip.cover}
                alt={`${tip.title} 爆料现场配图`}
                title={`${tip.title} - 爆料专区配图`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-8 flex flex-col gap-5">
              {tip.content.map((paragraph, index) => (
                <p key={`p-${index}`} className="text-[15px] leading-8 text-foreground-800 md:text-base">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <i className="ri-price-tag-3-line text-foreground-400" aria-hidden="true" />
              {tip.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/feed?q=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-background-200 px-3 py-1 font-label text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-200 text-secondary-800">
                  <i className="ri-user-3-line text-lg" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-label text-sm font-medium text-foreground-900">{tip.author}</p>
                  <p className="font-label text-xs text-foreground-500">匿名爆料人 · 保护隐私是我们的底线</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setLiked((prev) => !prev)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-4 py-2.5 font-label text-sm transition-colors ${
                    liked
                      ? "border-primary-300 bg-primary-100 text-primary-700"
                      : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  <i className={liked ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true" />
                  {tip.likes + (liked ? 1 : 0)}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-share-forward-line" aria-hidden="true" />
                  分享
                </button>
              </div>
            </div>
            {shareNotice ? (
              <p className="mt-3 font-label text-xs text-primary-700">{shareNotice}</p>
            ) : null}

            <CommentSection initial={initialComments} />
          </article>

          <aside className="flex flex-col gap-6 lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 lg:sticky lg:top-24">
              <h2 className="font-display text-base text-foreground-950">按分类继续逛</h2>
              <ul className="mt-4 flex flex-col gap-1">
                {categories.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={`/feed?category=${item.slug}`}
                      className="flex items-center justify-between rounded-md px-3 py-2 font-label text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                    >
                      <span className="inline-flex items-center gap-2">
                        <i className={`${item.icon} text-base`} aria-hidden="true" />
                        {item.label}
                      </span>
                      <span className="text-xs text-foreground-400">{item.count}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                <i className="ri-chat-private-line text-lg" aria-hidden="true" />
              </span>
              <h2 className="mt-4 font-display text-base text-foreground-950">私信通道</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                还有图片、录音或不方便公开的补充材料？通过私信通道直接发给我们，全程匿名。
              </p>
              <Link
                to="/submit"
                className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap font-label text-sm text-primary-700 transition-colors hover:text-primary-800"
              >
                进入私信通道
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-edit-2-line text-lg" aria-hidden="true" />
              </span>
              <h2 className="mt-4 font-display text-base text-foreground-950">也来说一条</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                读完有类似经历？写下时间、地点和经过，编辑会帮你整理成一篇完整的爆料。
              </p>
              <Link
                to="/submit"
                className="mt-4 inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                我要爆料
              </Link>
            </div>
          </aside>
        </div>

        <RelatedTips list={relatedList} />
      </div>
    </div>
  );
}